import React from 'react';
import type { PrestigeStatus } from '../../types/gameState';
import { formatNumber } from '../../utils/formatNumber';

interface StatusBarProps {
  totalYieldPerSecond: number;
  basicOre: number;
  prestigeStatus?: PrestigeStatus | null;
}

const tileStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--color-bg-secondary, #1a2638)',
  borderRadius: 'var(--radius-md, 5px)',
  border: '1px solid var(--color-border-light, #334466)',
  padding: '0.5rem 0',
  position: 'relative',
  overflow: 'hidden',
};

const StatusBar: React.FC<StatusBarProps> = ({ totalYieldPerSecond, basicOre, prestigeStatus }) => {
  const score = prestigeStatus?.currentPrestigeScore ?? 0;
  const threshold = prestigeStatus?.prestigeThreshold ?? 0;
  const progress = threshold > 0 ? Math.min(100, (score / threshold) * 100) : 0;
  const ready = prestigeStatus?.canPrestige ?? false;

  return (
    <div
      className="resources"
      style={{ display: 'flex', gap: 'var(--space-md)', width: '100%', boxSizing: 'border-box' }}
    >
      <div className="resource-display" style={tileStyle}>
        <div className="resource-label">Total Yield</div>
        <div className="resource-value">{formatNumber(totalYieldPerSecond)} m³/s</div>
      </div>
      <div className="resource-display" style={tileStyle}>
        <div className="resource-label">Ore</div>
        <div className="resource-value">{Math.round(basicOre).toLocaleString()} m³</div>
      </div>
      <div
        className="resource-display"
        style={tileStyle}
        title={`${Math.round(score).toLocaleString()} / ${Math.round(threshold).toLocaleString()} ore mined this run`}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${progress}%`,
            background: ready ? 'rgba(255, 200, 40, 0.25)' : 'rgba(80, 160, 255, 0.18)',
            transition: 'width 0.6s ease',
          }}
        />
        <div className="resource-label" style={{ position: 'relative' }}>
          {ready ? 'Prestige Ready!' : `Prestige ${(prestigeStatus?.prestigeLevel ?? 0) + 1}`}
        </div>
        <div className="resource-value" style={{ position: 'relative' }}>
          {formatNumber(score)} / {formatNumber(threshold)}
        </div>
      </div>
    </div>
  );
};

export default StatusBar;
